import { useCallback, useEffect, useRef, useState } from 'react';
import { useWebSocket } from '../WebSocketContext';

// Fetch private evidence only when its tooltip/dialog is actually open.
export default function useConnectorPreparationStep(item, enabled, { source = false } = {}) {
  const { sendMessage, subscribeToMessages } = useWebSocket();
  const step = item?.preparationStep;
  const caseid = item?.preparationCaseId;
  const datasourceId = item?.datasource?.id;
  const key = JSON.stringify([caseid, datasourceId, source, step?.stepId, step?.revision]);
  const [state, setState] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const pending = useRef(null);
  const timer = useRef(null);
  const fetchedAt = useRef(0);
  const activeView = useRef('input');
  const fingerprint = useRef(null);
  const collectionScope = useRef(null);
  fingerprint.current = state?.fingerprint;
  collectionScope.current = state?.collection_scope;
  const send = useCallback((command, extra = {}) => {
    if (!enabled || !datasourceId || (!source && !step?.stepId) || pending.current) return false;
    const id = `step-${Date.now()}-${Math.random().toString(36).slice(2)}`;
    pending.current = { id, command, append: Boolean(extra.cursor) };
    setBusy(true); setError('');
    if (sendMessage({ command, caseid, datasource_id: datasourceId,
      ...(!source ? { step_id: step.stepId, step_revision: step.contract } : {}), request_id: id,
      fingerprint: fingerprint.current, collection_scope: collectionScope.current, step_view: activeView.current, ...extra }) === false) {
      pending.current = null; setBusy(false); setError('setup_unavailable'); return false;
    }
    timer.current = setTimeout(() => {
      pending.current = null; setBusy(false); setError('timeout');
    }, 45000);
    return true;
  }, [enabled, source, caseid, datasourceId, step?.stepId, step?.contract, sendMessage]);
  const load = useCallback((cursor = '', view = 'input') => {
    if (pending.current) return false;
    if (view !== activeView.current) setState((previous) => previous ? { ...previous, items: [], next_cursor: '' } : null);
    activeView.current = view;
    return send('getDatasourcePipeline', { cursor, step_view: view });
  }, [send]);
  const review = useCallback((itemId, decision) => send('reviewDatasourcePipelineItem', {
    item_id: itemId, decision,
  }), [send]);
  useEffect(() => {
    fetchedAt.current = 0; setState(null); setError('');
  }, [key]);
  useEffect(() => {
    if (!enabled || (!source && !step?.stepId)) return undefined;
    const unsubscribe = subscribeToMessages((message) => {
      const data = message.data || {};
      if (message.command === 'datasourceIngestionUpdate'
          && String(data.caseid) === String(caseid)
          && String(data.datasourceId || data.datasource_id) === String(datasourceId)) {
        if (!pending.current && (data.status === 'idle' || Date.now() - fetchedAt.current > 5000)) {
          load('', activeView.current);
        }
        return;
      }
      if (!pending.current || message.command !== pending.current.command
          || data.request_id !== pending.current.id || String(data.caseid) !== String(caseid)
          || String(data.datasourceId) !== String(datasourceId)) return;
      const append = pending.current.append;
      clearTimeout(timer.current); pending.current = null; setBusy(false);
      if (data.status !== 'success') { setError(data.code || 'setup_unavailable'); return; }
      fetchedAt.current = Date.now();
      setState((previous) => ({ ...data, items: append && previous?.collection_scope === data.collection_scope
        ? [...(previous?.items || []), ...data.items] : data.items }));
    });
    if (Date.now() - fetchedAt.current > 30000) load('', activeView.current);
    return () => {
      unsubscribe?.(); clearTimeout(timer.current); pending.current = null; setBusy(false);
    };
  }, [enabled, source, key, caseid, datasourceId, step?.stepId, load, subscribeToMessages]);
  return { state, busy, error, load, review };
}
